import { getSettings } from '../../queries.js';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import { PatientTherapySequenceStatus } from '../../database/schema.js';
import { zql } from '../../schema.js';
import { inUseGfrQuery } from '../../util/patient-gfr.js';
import { addDaysUtc, EventLogs, getDosingWeight, getDrugRoundingMode, getRecalculatedTotalAmount, RECALCULATED_STATUSES, } from '../../utils.js';
import { nextLocalPrescriptionCode } from '../patient.mutators.js';
import { addEventLog, createPatchWithOldValues, EventLogResources } from '../util.js';
import { logAmmsMfnChangeForSequence } from './add-therapy-parameter.js';
import { getDoseBasis } from './dose-basis.js';
import { medicineLogItems, noteVariant, sequenceLogItems, sequenceNameItems } from './medicine-log-items.js';
import { itemsDetail } from '../../util/event-log-items.js';
import { computeTherapyDateFromCycleDay } from '../../util/protocol-cycles.js';
/**
 * Recalculates the total amounts of the patient's prescriptions not yet validated by a doctor,
 * then re-announces to AMMS (MFN MUP) every prescription whose amounts changed, plus
 * `editedSequenceIds` (prescriptions the caller just edited). With `recalculationLog`, the changed
 * amounts are also recorded as one summary event.
 */
export async function medicineUpdateTotalAmounts(tx, patientId, editedSequenceIds = [], recalculationLog) {
    const characteristics = await tx.run(zql.patientCharacteristics
        .where('patientId', '=', patientId)
        .related('patient')
        .orderBy('createdAt', 'desc')
        .limit(1)
        .one());
    const gfr = await tx.run(inUseGfrQuery(patientId));
    const medicines = await tx.run(zql.patientTherapySequenceMedicine
        .where('patientId', '=', patientId)
        // a remake keeps the dose the doctor validated on the original until the doctor changes it (CY-303)
        .whereExists('therapySequence', sequence => sequence
        .where('status', 'IN', RECALCULATED_STATUSES)
        .where('doseCopiedFromSequenceId', 'IS', null)
        .where('deletedAt', 'IS', null))
        .where('deletedAt', 'IS', null)
        .related('therapySequence')
        .related('activeSubstance', drug => drug.related('activeSubstanceDetails').related('supportiveMedicationDetails')));
    const changedSequenceIds = new Set(editedSequenceIds);
    const recalculatedSequenceIds = new Set();
    const changedDoses = [];
    await Promise.all(medicines.map(async (med) => {
        const totalAmount = getRecalculatedTotalAmount(med, {
            height: characteristics?.height,
            weight: getDosingWeight(characteristics),
            bsa: characteristics?.bsa,
            gfr: gfr?.gfr,
        }, getDrugRoundingMode(med.activeSubstance));
        if (totalAmount === null)
            return;
        recalculatedSequenceIds.add(med.therapySequenceId);
        if (totalAmount !== med.totalAmount) {
            changedSequenceIds.add(med.therapySequenceId);
            const label = [med.therapySequence?.internalId ?? med.therapySequenceId, med.activeSubstance?.name]
                .filter(Boolean)
                .join(' ');
            changedDoses.push({ label, oldAmount: med.totalAmount, newAmount: totalAmount });
        }
        await tx.mutate.patientTherapySequenceMedicine.update({ id: med.id, totalAmount });
    }));
    // Refreshed even when the amounts come out the same: they are now calculated with these values.
    const doseBasis = getDoseBasis(characteristics, gfr);
    await Promise.all([...recalculatedSequenceIds].map(id => tx.mutate.patientTherapySequence.update({ id, ...doseBasis })));
    const now = Date.now();
    await Promise.all([...changedSequenceIds].map(sequenceId => logAmmsMfnChangeForSequence(tx, { sequenceId, change: 'update', now })));
    if (recalculationLog && changedDoses.length > 0 && tx.location === 'server') {
        await logDoseRecalculation(tx, patientId, changedDoses, recalculationLog, now);
    }
}
async function logDoseRecalculation(tx, patientId, changedDoses, { cause, userId, impersonatedBy }, now) {
    const patient = await tx.run(zql.patients
        .where('id', '=', patientId)
        .related('identifiers', identifiers => identifiers.where('deletedAt', 'IS', null))
        .one());
    // One entry per prescription, keyed by its label; the `test` op keeps the old amount readable without earlier logs.
    const patches = changedDoses
        .toSorted((a, b) => a.label.localeCompare(b.label))
        .flatMap(({ label, oldAmount, newAmount }) => {
        const path = `/${label.replaceAll('~', '~0').replaceAll('/', '~1')}`;
        return [
            { op: 'test', path, value: oldAmount },
            { op: 'replace', path, value: newAmount },
        ];
    });
    await addEventLog(tx, {
        id: uuidv7(),
        resource: EventLogResources.patientDoseRecalculation,
        resourceId: patientId,
        action: 'update',
        domain: 'patient',
        event: EventLogs.PATIENT_DOSES_RECALCULATED,
        patches,
        performedBy: userId,
        performedAt: now,
        status: 'INFO',
        details: {
            code: patient?.identifiers[0]?.value,
            name: patient?.fullName,
            cause,
            count: String(changedDoses.length),
        },
        impersonatedBy: impersonatedBy ?? null,
    });
}
export async function removeUnusedMedicines(tx, userId, prevMedicineIds, currentMedicineIds) {
    const now = Date.now();
    const removedMedicineIds = prevMedicineIds.filter(id => !currentMedicineIds.includes(id));
    await Promise.all(removedMedicineIds.map(id => tx.mutate.patientTherapySequenceMedicine.update({
        id,
        deletedAt: now,
        deletedBy: userId,
    })));
}
export async function updatePatientProtocolTherapy(tx, input, userId) {
    const now = Date.now();
    const prev = await tx.run(zql.patientTherapySequence
        .where('id', '=', input.id)
        .related('medicine', q => q.where('deletedAt', 'IS', null))
        .related('protocol')
        .related('patientProtocol')
        .one());
    if (!prev) {
        throw new Error(`Patient Therapy sequence not found: ${input.id}`);
    }
    if (prev.status !== PatientTherapySequenceStatus.enum.pending_doctor_validation &&
        prev.status !== PatientTherapySequenceStatus.enum.pending_approval) {
        throw new Error('Cannot update a therapy sequence that has already been validated by a doctor');
    }
    if (!prev.cycle) {
        throw new Error(`Patient Therapy sequence does not belongs to any protocol: ${input.id}`);
    }
    if (!Number.isFinite(input.day) || input.day < 0) {
        throw new Error('Day must be provided when patientProtocolId is provided');
    }
    if (prev.protocol && input.day > prev.protocol.daysRepetition) {
        throw new Error(`Day must be less than protocol duration days (${prev.protocol.daysRepetition})`);
    }
    if (!prev.patientProtocol || !prev.protocol) {
        throw new Error(`Patient Protocol not found`);
    }
    const patient = await tx.run(zql.patients.where('id', '=', input.patientId).one());
    const { daysRepetition } = prev.protocol;
    const settings = await getSettings(tx);
    const isZeroDayProtocol = settings?.dayZeroProtocol;
    // a moved cycle keeps its own start date (CY-378)
    const therapyDate = computeTherapyDateFromCycleDay(prev.patientProtocol, prev.cycle, input.day, daysRepetition, isZeroDayProtocol);
    const sequenceUpdate = {
        id: input.id,
        patientId: input.patientId,
        protocolId: prev.patientProtocol.protocolId,
        patientProtocolId: prev.patientProtocol.id,
        therapyDate,
        day: input.day,
        status: PatientTherapySequenceStatus.enum.pending_doctor_validation,
        isNote: false,
        time: input.time,
        carrierSolutionId: input.carrierSolutionId[0] ?? undefined,
        applicationFormId: input.applicationFormId[0] ?? undefined,
        duration: input.duration ?? undefined,
        volume: input.volume ?? undefined,
        volumeMode: input.volumeMode[0] ?? undefined,
        updatedAt: now,
        updatedBy: userId,
    };
    await tx.mutate.patientTherapySequence.update(sequenceUpdate);
    if (tx.location === 'server') {
        const patches = createPatchWithOldValues(prev, sequenceUpdate);
        const items = [...sequenceLogItems(prev, sequenceUpdate), ...(await sequenceNameItems(tx, prev, sequenceUpdate))];
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: input.id,
            action: 'update',
            domain: 'prescription',
            event: EventLogs.PATIENT_THERAPY_SEQUENCE_PROTOCOL_UPDATED,
            patches,
            performedBy: userId,
            performedAt: now,
            details: {
                therapyInternalId: prev.internalId,
                patientName: patient?.fullName ?? '',
                patientId: input.patientId,
                variant: noteVariant(items),
                ...itemsDetail(items),
            },
        });
    }
    const medicineIds = [];
    const sequenceOps = [];
    const logSequenceOps = [];
    for (const med of input.medicine) {
        const id = med.id || uuidv7();
        medicineIds.push(id);
        const medicineData = {
            id,
            therapySequenceId: input.id,
            patientId: input.patientId,
            therapyDate,
            activeSubstanceId: med.activeSubstanceId[0] ?? undefined,
            calculationMethod: med.calculationMethod[0] ?? undefined,
            doseProportion: med.doseProportion ?? undefined,
            prescribedNameId: med.prescribedNameId[0] ?? undefined,
            autIdem: med.prescribedNameId[0] ? (med.autIdem ?? false) : false,
            regularDose: med.regularDose,
            precisionDecimals: med.precisionDecimals,
            notes: med.notes ?? null,
            ageRanges: med.ageRanges ?? undefined,
            updatedAt: now,
            updatedBy: userId,
        };
        sequenceOps.push(() => tx.mutate.patientTherapySequenceMedicine.upsert(medicineData));
        if (tx.location === 'server') {
            const patches = createPatch({}, medicineData);
            const prevMedicine = prev.medicine.find(m => m.id === id);
            logSequenceOps.push(async () => addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientTherapySequence,
                resourceId: medicineData.id,
                action: prevMedicine ? 'update' : 'create',
                domain: 'prescription',
                event: prevMedicine
                    ? EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_PROTOCOL_UPDATED
                    : EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_PROTOCOL_ADDED,
                patches,
                performedBy: userId,
                performedAt: now,
                details: {
                    patientId: patient?.id ?? '',
                    patientName: patient?.fullName ?? '',
                    therapyInternalId: prev?.internalId ?? '',
                    ...itemsDetail(await medicineLogItems(tx, prevMedicine, medicineData)),
                },
            }));
        }
    }
    await Promise.all(sequenceOps.map(op => op()));
    await Promise.all(logSequenceOps.map(op => op()));
    const prevMedicineIds = prev.medicine.map(m => m.id);
    await removeUnusedMedicines(tx, userId, prevMedicineIds, medicineIds);
    if (prev.sequenceGroupId && prev.patientProtocolId && prev.patientProtocol.startDate) {
        const siblingSequences = await tx.run(zql.patientTherapySequence
            .where('sequenceGroupId', '=', prev.sequenceGroupId)
            .where('status', '=', PatientTherapySequenceStatus.enum.pending_doctor_validation)
            .where('deletedAt', 'IS', null));
        const therapySequenceIds = siblingSequences.map(seq => seq.id);
        const siblingMedicines = await tx.run(zql.patientTherapySequenceMedicine
            .where('therapySequenceId', 'IN', therapySequenceIds)
            .where('deletedAt', 'IS', null));
        const settings = await getSettings(tx);
        const isZeroDayProtocol = settings?.dayZeroProtocol;
        const sequenceOps = [];
        if (prev.cycle) {
            for (const seq of siblingSequences) {
                if (!seq.cycle || seq.cycle < prev.cycle)
                    continue;
                const day = isZeroDayProtocol ? input.day : input.day - 1;
                const siblingCycleOffset = (seq.cycle - 1) * daysRepetition;
                const siblingTherapyDate = addDaysUtc(prev.patientProtocol.startDate, siblingCycleOffset + day);
                sequenceOps.push(() => tx.mutate.patientTherapySequence.update({
                    id: seq.id,
                    day: input.day,
                    therapyDate: siblingTherapyDate,
                    updatedAt: now,
                    updatedBy: userId,
                }));
                const sequnceMedicine = siblingMedicines.filter(med => med.therapySequenceId === seq.id);
                for (const med of sequnceMedicine) {
                    sequenceOps.push(() => tx.mutate.patientTherapySequenceMedicine.update({
                        id: med.id,
                        therapyDate: siblingTherapyDate,
                        updatedAt: now,
                        updatedBy: userId,
                    }));
                }
            }
        }
        await Promise.all(sequenceOps.map(op => op()));
    }
    await medicineUpdateTotalAmounts(tx, input.patientId);
}
export async function updatePatientPrescriptionTherapy(tx, input, userId) {
    const now = Date.now();
    const prev = await tx.run(zql.patientTherapySequence
        .where('id', '=', input.id)
        .related('medicine', q => q.where('deletedAt', 'IS', null))
        .one());
    if (!prev) {
        throw new Error(`Patient Therapy sequence not found: ${input.id}`);
    }
    if (prev.status !== PatientTherapySequenceStatus.enum.pending_doctor_validation &&
        prev.status !== PatientTherapySequenceStatus.enum.pending_approval) {
        throw new Error('Cannot update a therapy sequence that has already been validated by a doctor');
    }
    if (!input.fixedDate) {
        throw new Error('Either patientProtocolId or fixedDate must be provided');
    }
    const patient = await tx.run(zql.patients.where('id', '=', input.patientId).one());
    const therapyDate = input.fixedDate;
    let internalId = prev.internalId;
    if (prev.therapyDate !== therapyDate || !prev.internalId) {
        const prescriptionDate = new Date(therapyDate);
        const internalIdStructure = await nextLocalPrescriptionCode(tx, `PR-${prescriptionDate.getFullYear()}${String(prescriptionDate.getMonth() + 1).padStart(2, '0')}${String(prescriptionDate.getDate()).padStart(2, '0')}`);
        internalId = `${internalIdStructure.prefixDash}${internalIdStructure.maxNum + 1}`;
    }
    const sequenceUpdate = {
        id: input.id,
        time: input.time,
        internalId,
        carrierSolutionId: input.carrierSolutionId[0] ?? undefined,
        applicationFormId: input.applicationFormId[0] ?? undefined,
        duration: input.duration ?? undefined,
        volume: input.volume ?? undefined,
        volumeMode: input.volumeMode[0] ?? undefined,
        updatedAt: now,
        updatedBy: userId,
        therapyDate,
    };
    await tx.mutate.patientTherapySequence.update(sequenceUpdate);
    if (tx.location === 'server') {
        const patches = createPatchWithOldValues({ ...prev, medicine: [] }, sequenceUpdate);
        const items = [...sequenceLogItems(prev, sequenceUpdate), ...(await sequenceNameItems(tx, prev, sequenceUpdate))];
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: input.id,
            action: 'update',
            domain: 'prescription',
            event: EventLogs.PATIENT_THERAPY_SEQUENCE_UPDATED,
            patches,
            performedBy: userId,
            performedAt: now,
            details: {
                id: internalId,
                patientName: patient?.fullName,
                patientId: input.patientId,
                therapyDate: therapyDate?.toString(),
                time: sequenceUpdate.time,
                variant: noteVariant(items),
                ...itemsDetail(items),
            },
        });
    }
    const medicineIds = [];
    const sequenceOps = [];
    const logSequenceOps = [];
    for (const med of input.medicine) {
        const id = med.id || uuidv7();
        medicineIds.push(id);
        const isUpdate = !!med.id;
        const medicineData = {
            id,
            therapySequenceId: input.id,
            patientId: input.patientId,
            therapyDate,
            activeSubstanceId: med.activeSubstanceId[0] ?? undefined,
            prescribedNameId: med.prescribedNameId[0] ?? undefined,
            autIdem: med.prescribedNameId[0] ? (med.autIdem ?? false) : false,
            regularDose: med.regularDose,
            precisionDecimals: med.precisionDecimals,
            doseProportion: med.doseProportion ?? undefined,
            calculationMethod: med.calculationMethod[0] ?? undefined,
            notes: med.notes ?? null,
            ageRanges: med.ageRanges ?? undefined,
            ...(isUpdate ? { updatedAt: now, updatedBy: userId } : { createdAt: now, createdBy: userId }),
        };
        sequenceOps.push(() => tx.mutate.patientTherapySequenceMedicine.upsert(medicineData));
        if (tx.location === 'server') {
            const patches = createPatch({}, medicineData);
            const prevMedicine = prev.medicine.find(m => m.id === id);
            logSequenceOps.push(async () => addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientTherapySequence,
                resourceId: medicineData.id,
                action: isUpdate ? 'update' : 'create',
                domain: 'prescription',
                event: prevMedicine
                    ? EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_UPDATED
                    : EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_ADDED,
                patches,
                performedBy: userId,
                performedAt: now,
                details: {
                    id: medicineData.id,
                    patientName: patient?.fullName ?? '',
                    patientId: patient?.id ?? '',
                    therapyInternalId: sequenceUpdate.internalId,
                    ...itemsDetail(await medicineLogItems(tx, prevMedicine, medicineData)),
                },
            }));
        }
    }
    await Promise.all(sequenceOps.map(op => op()));
    await Promise.all(logSequenceOps.map(op => op()));
    const prevMedicineIds = prev.medicine.map(m => m.id);
    await removeUnusedMedicines(tx, userId, prevMedicineIds, medicineIds);
    await medicineUpdateTotalAmounts(tx, input.patientId);
}
//# sourceMappingURL=medicine-update-total-amounts.js.map